import { apiRequest } from './api';

/** Admin YOYO Picks (PRD §5.8). */
export interface AdminPickRow {
  id: string;
  slug: string;
  type: 'PROVIDER' | 'EVENT';
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  titleCN: string | null;
  titleEN: string | null;
  providerName: string;
  ageMin: number;
  ageMax: number;
  participation: string;
  isFeatured: boolean;
  order: number | null;
  recommendCount: number;
  notRecommendCount: number;
  sourceKind: string;
  updatedAt: string;
  ended: boolean;
  linkClicks30d: number;
  calendarClicks30d: number;
  favorites: number;
}

export interface AdminOccurrence {
  id: string;
  startTime: string;
  endTime: string;
  noteCN: string | null;
  noteEN: string | null;
}

/** A full Pick as the admin form edits it. */
export interface AdminPick {
  id: string;
  slug: string;
  type: 'PROVIDER' | 'EVENT';
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  titleCN: string | null;
  titleEN: string | null;
  titleAutoLang: 'ZH' | 'EN' | null;
  descriptionCN: string | null;
  descriptionEN: string | null;
  descriptionAutoLang: 'ZH' | 'EN' | null;
  whyPickedCN: string | null;
  whyPickedEN: string | null;
  whyPickedAutoLang: 'ZH' | 'EN' | null;
  providerName: string;
  externalUrl: string;
  externalBookingNoteCN: string | null;
  externalBookingNoteEN: string | null;
  externalBookingNoteAutoLang: 'ZH' | 'EN' | null;
  region: string[];
  cityCN: string | null;
  cityEN: string | null;
  cityAutoLang: 'ZH' | 'EN' | null;
  /** Legacy single city, read only while a row has no pair yet (deploy window). */
  city?: string | null;
  addressLine: string | null;
  ageMin: number;
  ageMax: number;
  participation: string;
  language: string;
  category: string;
  priceNoteCN: string | null;
  priceNoteEN: string | null;
  priceNoteAutoLang: 'ZH' | 'EN' | null;
  scheduleNoteCN: string | null;
  scheduleNoteEN: string | null;
  scheduleNoteAutoLang: 'ZH' | 'EN' | null;
  isFeatured: boolean;
  order: number | null;
  occurrences: AdminOccurrence[];
  submissionStatus: 'PENDING' | 'APPROVED' | 'REJECTED' | 'MERGED' | null;
  submitterNote: string | null;
  submittedBy: { id: string; email: string; name: string | null } | null;
  rejectionReason: string | null;
  /** Intake trace; searchSources is set when the draft came from a web search. */
  source?: { searchSources?: string[] } | null;
}

export interface AdminSubmission extends AdminPick {
  reviews: { id: string; userId: string; content: string; visited: boolean; status: string }[];
  createdAt: string;
}

/** The writable fields (the server re-validates everything). */
export type AdminPickPayload = Omit<
  AdminPick,
  | 'id'
  | 'slug'
  | 'status'
  | 'occurrences'
  | 'submissionStatus'
  | 'submitterNote'
  | 'submittedBy'
  | 'rejectionReason'
  | 'source'
  | 'city'
  // Featured and order are set in the dashboard's Featured YOYO Picks list.
  | 'isFeatured'
  | 'order'
> & {
  occurrences: {
    startTime: string;
    endTime: string;
    noteCN: string | null;
    noteEN: string | null;
  }[];
};

/** A published Pick in the dashboard's Featured YOYO Picks list. */
export interface FeaturablePick {
  id: string;
  type: 'PROVIDER' | 'EVENT';
  titleCN: string | null;
  titleEN: string | null;
  providerName: string;
  isFeatured: boolean;
  order: number | null;
}

export const adminPicksApi = {
  async featured(): Promise<FeaturablePick[]> {
    return (await apiRequest<{ data: FeaturablePick[] }>('/admin/featured-picks')).data;
  },
  /** The featured Picks in homepage order; every other Pick stops being featured. */
  async setFeatured(ids: string[]) {
    return apiRequest('/admin/featured-picks', {
      method: 'PUT',
      body: JSON.stringify({ ids }),
    });
  },
  async list(params: { status?: string; q?: string } = {}): Promise<AdminPickRow[]> {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.q) qs.set('q', params.q);
    const res = await apiRequest<{ data: AdminPickRow[] }>(`/admin/picks?${qs.toString()}`);
    return res.data;
  },
  async get(id: string): Promise<AdminPick> {
    return (await apiRequest<{ data: AdminPick }>(`/admin/picks/${id}`)).data;
  },
  async create(payload: AdminPickPayload, publish: boolean) {
    return (
      await apiRequest<{ data: AdminPick }>('/admin/picks', {
        method: 'POST',
        body: JSON.stringify({ ...payload, publish }),
      })
    ).data;
  },
  async update(id: string, payload: AdminPickPayload) {
    return (
      await apiRequest<{ data: AdminPick }>(`/admin/picks/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      })
    ).data;
  },
  async setStatus(id: string, status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED') {
    return apiRequest(`/admin/picks/${id}/status`, {
      method: 'POST',
      body: JSON.stringify({ status }),
    });
  },
  async submissions(status = 'PENDING'): Promise<AdminSubmission[]> {
    return (
      await apiRequest<{ data: AdminSubmission[] }>(`/admin/picks/submissions?status=${status}`)
    ).data;
  },
  async approve(id: string) {
    return apiRequest(`/admin/picks/submissions/${id}/approve`, { method: 'POST' });
  },
  async reject(id: string, reason: string) {
    return apiRequest(`/admin/picks/submissions/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },
  async merge(id: string, targetPickId: string) {
    return apiRequest(`/admin/picks/submissions/${id}/merge`, {
      method: 'POST',
      body: JSON.stringify({ targetPickId }),
    });
  },
};
